import { useEffect } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { PosMicrosIntegrationPaymentMapping } from '@mews-commander/data-access';
import { LocalizedText, lt } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Button, Card, CardContent, IconButton, IconName, Typography, Title, Stack } from '@optimus-web/core';
import { FieldNames } from '../fieldNames';
import { MicrosOutletFormSchema } from '../microsOutlets/formProps';
import { PaymentMappingForm } from './PaymentMappingForm';

/**
 * `getValues` from schema doesn't return all the values for fields, so we receive issue with uncontrolled fields became controlled
 * and internal form functionality became corrupted.
 * Here we had an issue with default values on adding a new payment mapping. If we removed last item with modified values
 * a new item added with those values instead of default values.
 * Need to cast this default values until we will clarify the solution around all default values generation from schema.
 */
const paymentMappingDefaultValues = {
    [FieldNames.paymentNumber]: '',
    [FieldNames.paymasterId]: '',
    [FieldNames.accountingCategoryId]: '',
    [FieldNames.externalPaymentType]: null,
    [FieldNames.isDefault]: false,
} as unknown as PosMicrosIntegrationPaymentMapping;

export const PaymentMappingsList = ({ formFormat }: { formFormat?: string }) => {
    const { setValue } = useFormContext<MicrosOutletFormSchema>();
    const { fields, append, remove } = useFieldArray<MicrosOutletFormSchema, 'paymentMappings'>({
        name: FieldNames.paymentMappings,
    });

    useEffect(() => {
        if (fields.length === 0) return;
        const defaultPaymentMappingIndex = fields.findIndex((paymentMapping) => paymentMapping?.isDefault);
        if (defaultPaymentMappingIndex < 0) {
            /** This sets the first default value checked as at least one default value should be checked */
            setValue(`${FieldNames.paymentMappings}.${0}.${FieldNames.isDefault}`, true);
        }
    }, [fields, setValue]);

    return (
        <Card title={TextKey.PaymentMappings}>
            <CardContent padding="200">
                <Stack alignment="start" distribution="space-between">
                    <Title level={2}>{lt('PaymentMappings')}</Title>
                    <Typography textStyle="bodyMediumStrong" as="div">
                        <LocalizedText
                            textKey={TextKey.PaymentMappingAddedItems}
                            substitutes={{ count: fields.length || 0 }}
                        />
                    </Typography>
                </Stack>
            </CardContent>
            <CardContent padding="200">
                <Stack vertical spacing="300">
                    {fields.map((field, index) => (
                        <Card key={field.id} padding="200">
                            <CardContent>
                                <Stack alignment="center" distribution="space-between">
                                    <PaymentMappingForm key={field.id} index={index} formFormat={formFormat} />
                                    <IconButton
                                        icon={IconName.Delete}
                                        type="button"
                                        variant="ghost"
                                        iconSize="small"
                                        onClick={() => {
                                            remove(index);
                                        }}
                                    />
                                </Stack>
                            </CardContent>
                        </Card>
                    ))}
                    <Stack alignment="start">
                        <Button
                            variant="tertiary"
                            icon={IconName.Add}
                            onClick={() => {
                                append(paymentMappingDefaultValues);
                            }}
                            type="submit"
                        >
                            {lt('AddPaymentMapping')}
                        </Button>
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
};
